import { useCollection } from '@amzn/awsui-collection-hooks';
import { Box, Button, Header, Pagination, SpaceBetween, Table, TableProps } from '@amzn/awsui-components-core';
import PropertyFilter from '@amzn/awsui-components-core/property-filter';
import React, { ReactNode, useCallback, useMemo } from 'react';
import { getPanelContent } from 'src/components/fintech-ops/risk-remediator/utils';
import { propertyFilterI18nStrings } from 'src/i18n-strings';
import { handleError, logger } from 'src/logger';
import { DEFAULT_FILE_NAME, DEFAULT_SHEET_NAME, formatTableDataForExport } from 'src/utilities/CloudscapeTableUtils';
import { exportAsExcelFile } from 'src/utilities/FileServices';
import { EmptyStateMessage } from '../EmptyStateMessage';

// Types
interface SelectedItem {
  applicationName: string;
  asr_application_name: string;
  [key: string]: any;
}

interface RiskDetailsTableProps {
  emptyMessage: ReactNode;
  filterProperties?: any;
  visibleContent?: readonly string[];
  data: readonly any[];
  columnDefinitions: TableProps.ColumnDefinition<any>[];
  pageSize?: number;
  headerName?: string;
  isLoading?: boolean;
  isSplitToggleEnabled: (isSplitPanelOpen: boolean, header: any, body: any) => void;
  shouldEnableSelection: boolean;
  defaultSortColumnKey: string;
  dataType: string;
  enableDownloadReport?: boolean;
  downloadFileName?: string;
}

const DEFAULT_PAGE_SIZE = 3;

export const getTextFilterCounterText = (count: number | undefined): string => `${count ?? 0} ${(count ?? 0) === 1 ? 'match' : 'matches'}`;

export const RiskDetailsTable: React.FC<RiskDetailsTableProps> = ({
  enableDownloadReport = false,
  filterProperties: initialFilterProperties,
  pageSize = DEFAULT_PAGE_SIZE,
  visibleContent,
  data,
  columnDefinitions,
  defaultSortColumnKey,
  dataType,
  isLoading,
  emptyMessage,
  headerName,
  shouldEnableSelection,
  isSplitToggleEnabled,
  downloadFileName
}) => {
  const [selectedItems, setSelectedItems] = React.useState<readonly SelectedItem[]>([]);

  const collectionHookConfig = useMemo(
    () => ({
      propertyFiltering: {
        filteringProperties: initialFilterProperties,
        empty: <EmptyStateMessage title="No data" subtitle="No data to display." />,
        noMatch: (
          <EmptyStateMessage
            title="No matches"
            subtitle="No match found to display."
            action={
              <Button variant="primary" onClick={() => actions.setFiltering('')}>
                Clear Filter
              </Button>
            }
          />
        )
      },
      pagination: { pageSize },
      sorting: {
        defaultState: {
          sortingColumn: { sortingField: defaultSortColumnKey },
          isDescending: true
        }
      },
      selection: {}
    }),
    [initialFilterProperties, pageSize, defaultSortColumnKey]
  );

  const { items, actions, filteredItemsCount, collectionProps, propertyFilterProps, paginationProps } = useCollection(data, collectionHookConfig);

  const handleSelectionChange = useCallback(
    ({ detail }: { detail: { selectedItems: SelectedItem[] } }) => {
      try {
        setSelectedItems(detail.selectedItems);
        const panelContent = getPanelContent(detail.selectedItems, 'single');
        isSplitToggleEnabled(true, panelContent.header, panelContent.body);

        logger.info('Item selection changed', {
          selectedCount: detail.selectedItems.length,
          firstSelectedItem: detail.selectedItems[0]?.applicationName
        });
      } catch (error) {
        handleError('Error handling selection change:', error);
      }
    },
    [isSplitToggleEnabled]
  );

  const handleDownload = useCallback(() => {
    logger.info('Starting download process');
    try {
      const exportData = formatTableDataForExport(data, columnDefinitions);
      const fileName = downloadFileName ?? DEFAULT_FILE_NAME;
      exportAsExcelFile(exportData, fileName, DEFAULT_SHEET_NAME);
      logger.info('Download completed successfully');
    } catch (error) {
      handleError('Error during download:', error);
    }
  }, [data, columnDefinitions, downloadFileName]);

  const renderEmptyState = useCallback(
    () => (
      <Box textAlign="center" color="inherit">
        <Box padding={{ bottom: 's' }} variant="p" color="inherit">
          {!items ? 'No risks to show' : emptyMessage}
        </Box>
      </Box>
    ),
    [items, emptyMessage]
  );

  const renderPagination = useCallback(
    () => (
      <SpaceBetween size="m" direction="horizontal">
        <Pagination {...paginationProps} />
        {enableDownloadReport && (
          <Button iconName="download" onClick={handleDownload} disabled={!data.length}>
            Download
          </Button>
        )}
      </SpaceBetween>
    ),
    [paginationProps, enableDownloadReport, handleDownload, data.length]
  );

  const tableProps = useMemo(
    () =>
      ({
        ...collectionProps,
        variant: 'embedded' as const,
        columnDefinitions,
        items,
        loading: isLoading,
        loadingText: `Loading ${dataType} Details`,
        visibleColumns: visibleContent,
        filter: (
          <PropertyFilter
            {...propertyFilterProps}
            countText={getTextFilterCounterText(filteredItemsCount)}
            expandToViewport={true}
            i18nStrings={propertyFilterI18nStrings(dataType)}
          />
        ),
        resizableColumns: true,
        stickyHeader: true,
        empty: renderEmptyState(),
        header: <Header counter={`(${data.length})`}>{headerName}</Header>,
        pagination: renderPagination(),
        ...(shouldEnableSelection && {
          selectedItems,
          selectionType: 'single' as const,
          ariaLabels: {
            selectionGroupLabel: 'Item selection',
            allItemsSelectionLabel: ({ selectedItems }: { selectedItems: SelectedItem[] }) =>
              `${selectedItems.length} ${selectedItems.length === 1 ? 'item' : 'items'} selected`,
            itemSelectionLabel: (_: any, item: SelectedItem) => item.applicationName
          },
          trackBy: 'asr_application_name' as const,
          onSelectionChange: handleSelectionChange
        })
      } as TableProps),
    [
      collectionProps,
      columnDefinitions,
      items,
      isLoading,
      dataType,
      visibleContent,
      propertyFilterProps,
      filteredItemsCount,
      renderEmptyState,
      data.length,
      headerName,
      renderPagination,
      shouldEnableSelection,
      selectedItems,
      handleSelectionChange
    ]
  );

  return <Table {...tableProps} />;
};

export default RiskDetailsTable;
